// ABOUTME: Firestore access for invite codes: redemption during sign-up, and admin management.
// ABOUTME: Redemption is one transaction that bumps the code's use count and creates the account.

import { db } from "./firebase";
import {
  collection,
  doc,
  deleteDoc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  inviteStatus,
  normalizeInviteCode,
  normalizeInviteRecord,
  prepareInviteInput,
  validateInviteInput,
  type InviteInput,
  type InviteRecord,
  type InviteStatus,
} from "../utils/invites";

const invitesRef = () => collection(db, "invites");

export interface SignUpRequest {
  email: string;
  firstName: string;
  lastName: string;
  zip: string;
  ip?: string;
  inviteCode: string;
}

/** Thrown when the code cannot be redeemed; `reason` picks the user-facing message. */
export class InviteError extends Error {
  constructor(public readonly reason: InviteStatus | "unknown") {
    super(`Invite code is ${reason}`);
    this.name = "InviteError";
  }
}

export class InviteValidationError extends Error {
  constructor(public readonly fields: ReturnType<typeof validateInviteInput>) {
    super("Invite has invalid fields: " + Object.keys(fields).join(", "));
    this.name = "InviteValidationError";
  }
}

export const inviteService = {
  /**
   * Redeems a code and creates the account record in one transaction.
   *
   * The client checks the code first so the form can say *why* it was
   * refused; the rules re-check everything (code exists, is live, `uses`
   * goes up by exactly one, the account carries the code), so a modified
   * client cannot skip the gate. Two people racing for the last use of a
   * code: the transaction re-reads and the second one gets "exhausted".
   */
  async redeem(request: SignUpRequest): Promise<void> {
    const code = normalizeInviteCode(request.inviteCode);
    if (!code) throw new InviteError("unknown");
    const inviteRef = doc(invitesRef(), code);
    const accountRef = doc(collection(db, "beta_list"));

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(inviteRef);
      if (!snap.exists()) throw new InviteError("unknown");
      const invite = normalizeInviteRecord(code, snap.data());
      const status = inviteStatus(invite, Date.now());
      if (status !== "live") throw new InviteError(status);

      tx.update(inviteRef, { uses: invite.uses + 1 });
      tx.set(accountRef, {
        email: request.email,
        firstName: request.firstName,
        lastName: request.lastName,
        zip: request.zip,
        ...(request.ip ? { ip: request.ip } : {}),
        inviteCode: code,
        requestedAt: new Date(),
        allowed: true,
      });
    });
  },

  // ---- Admin ----

  subscribeToInvites(
    callback: (invites: InviteRecord[]) => void,
    onError?: (error: Error) => void
  ) {
    const q = query(invitesRef(), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.docs.map((d) => normalizeInviteRecord(d.id, d.data())));
      },
      (error) => {
        console.error("Invites subscription failed:", error);
        onError?.(error);
      }
    );
  },

  async createInvite(input: InviteInput): Promise<string> {
    const prepared = prepareInviteInput(input);
    const errors = validateInviteInput(prepared);
    if (Object.keys(errors).length > 0) throw new InviteValidationError(errors);
    await setDoc(doc(invitesRef(), prepared.code), {
      label: prepared.label,
      maxUses: prepared.maxUses,
      uses: 0,
      active: prepared.active,
      expiresAt: prepared.expiresAt,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return prepared.code;
  },

  async setInviteActive(code: string, active: boolean): Promise<void> {
    await updateDoc(doc(invitesRef(), code), { active, updatedAt: serverTimestamp() });
  },

  async deleteInvite(code: string): Promise<void> {
    await deleteDoc(doc(invitesRef(), code));
  },
};
